import { markRaw, ref, shallowRef, type Ref } from 'vue'
import { all as getAllCountryCodes, type CountryData } from 'country-codes-list'
import { useAxios } from '@/composables/useAxios'
import { STATUS_CODES } from '@/constants'
import type {
  Country,
  Subdivision,
  CountriesResponse,
  SubdivisionsResponse,
  StateSuggestion,
  CountryWithFlag
} from '@/types'

// Module-level cache for countries (static data, rarely changes)
let countriesCache: CountryWithFlag[] | null = null
let countriesPromise: Promise<CountryWithFlag[]> | null = null

// Module-level cache for subdivisions per country
const subdivisionsCache = new Map<string, Subdivision[]>()
const subdivisionsPromises = new Map<string, Promise<Subdivision[]>>()

// Lazy-loaded country-codes-list data
let countryCodesListData: CountryData[] | null = null

function getCountryCodesListData(): CountryData[] {
  if (!countryCodesListData) {
    countryCodesListData = getAllCountryCodes()
  }
  return countryCodesListData
}

/**
 * Merge API countries with country-codes-list data for flags and calling codes
 */
function mergeCountriesWithFlags(apiCountries: Country[]): CountryWithFlag[] {
  const countryCodesList = getCountryCodesListData()
  const countryCodesMap = new Map(
    countryCodesList.map((country) => [country.countryCode.toUpperCase(), country])
  )

  // markRaw on each item: static reference data fed into Vuetify v-autocomplete
  // dropdowns (250+ items). Deep proxies on hundreds of items amplified a
  // Firefox render loop on v-autocomplete blur via Vuetify's transformIn
  // fallback path. Plain objects are sufficient — nothing mutates them.
  return apiCountries.map((apiCountry) => {
    const countryData = countryCodesMap.get(apiCountry.code.toUpperCase())
    return markRaw({
      code: apiCountry.code,
      name: apiCountry.name,
      flag: countryData?.flag ?? '',
      callingCode: countryData?.countryCallingCode ?? ''
    })
  })
}

/**
 * Fetch and cache all countries from API, merged with flags
 */
export async function fetchCountries(): Promise<CountryWithFlag[]> {
  // Return cached data if available
  if (countriesCache) {
    return countriesCache
  }

  // Return existing promise if fetch in progress
  if (countriesPromise) {
    return countriesPromise
  }

  // Start new fetch
  countriesPromise = (async () => {
    try {
      const res = await useAxios<null, CountriesResponse>(
        'GET',
        '/geo/countries',
        false // No auth required
      )

      if (res?.status === STATUS_CODES.OK && res.data?.countries) {
        const mergedCountries = mergeCountriesWithFlags(res.data.countries)
        countriesCache = mergedCountries
        return mergedCountries
      }

      throw new Error('Invalid countries response')
    } finally {
      countriesPromise = null
    }
  })()

  return countriesPromise
}

/**
 * Fetch and cache subdivisions for a specific country
 */
export async function fetchSubdivisions(countryCode: string): Promise<Subdivision[]> {
  const cacheKey = countryCode.toUpperCase()

  // Return cached data if available
  if (subdivisionsCache.has(cacheKey)) {
    return subdivisionsCache.get(cacheKey)!
  }

  // Return existing promise if fetch in progress
  if (subdivisionsPromises.has(cacheKey)) {
    return subdivisionsPromises.get(cacheKey)!
  }

  // Start new fetch
  const fetchPromise = (async () => {
    try {
      const res = await useAxios<null, SubdivisionsResponse>(
        'GET',
        `/geo/subdivisions?country=${cacheKey}`,
        false // No auth required
      )

      if (res?.status === STATUS_CODES.OK && res.data?.subdivisions) {
        // markRaw each item: same rationale as countries.
        const raw = res.data.subdivisions.map(s => markRaw(s))
        subdivisionsCache.set(cacheKey, raw)
        return raw
      }

      // Return empty array for countries without subdivisions
      subdivisionsCache.set(cacheKey, [])
      return []
    } catch {
      // Return empty array on error
      return []
    } finally {
      subdivisionsPromises.delete(cacheKey)
    }
  })()

  subdivisionsPromises.set(cacheKey, fetchPromise)
  return fetchPromise
}

/**
 * Suggest state based on postcode and optionally city
 */
export async function suggestState(
  countryCode: string,
  postcode: string,
  city?: string
): Promise<StateSuggestion | null> {
  const params = new URLSearchParams({
    country: countryCode.toUpperCase(),
    postcode: postcode.trim()
  })

  if (city?.trim()) {
    params.append('city', city.trim())
  }

  try {
    const res = await useAxios<null, StateSuggestion>(
      'GET',
      `/geo/suggest-state?${params.toString()}`,
      false // No auth required
    )

    if (res?.status === STATUS_CODES.OK && res.data?.code) {
      return res.data
    }

    return null
  } catch {
    // API returns 404 when no suggestion available - this is expected
    return null
  }
}

/**
 * Get calling code for a country (for auto-selecting phone country code)
 */
export function getCallingCodeForCountry(countryCode: string): string | null {
  const countryCodesList = getCountryCodesListData()
  const country = countryCodesList.find(
    (c) => c.countryCode.toUpperCase() === countryCode.toUpperCase()
  )
  return country?.countryCallingCode ?? null
}

/**
 * Get full CountryData from country-codes-list by country code
 * Useful for phone number dropdowns that need the full object
 */
export function getCountryDataByCode(countryCode: string): CountryData | null {
  const countryCodesList = getCountryCodesListData()
  return (
    countryCodesList.find((c) => c.countryCode.toUpperCase() === countryCode.toUpperCase()) ?? null
  )
}

/**
 * Get all country codes data (for phone dropdowns that need full CountryData)
 */
export function getAllCountryCodesData(): CountryData[] {
  return [...getCountryCodesListData()].sort((a, b) =>
    a.countryNameEn.localeCompare(b.countryNameEn)
  )
}

/**
 * Composable for using geo data in components
 */
export function useGeo() {
  // shallowRef for the items arrays: each item is already markRaw'd (countries)
  // or markRaw'd on assignment (subdivisions, below). Avoids deep-proxying
  // hundreds of static reference items — see Firefox v-autocomplete blur loop.
  const countries: Ref<CountryWithFlag[]> = shallowRef([])
  const countriesLoading = ref(false)
  const countriesError = ref<string | null>(null)

  const subdivisions: Ref<Subdivision[]> = shallowRef([])
  const subdivisionsLoading = ref(false)
  const subdivisionsError = ref<string | null>(null)
  const currentCountryCode = ref<string>('')

  /**
   * Load countries list with flags
   */
  const loadCountries = async () => {
    if (countries.value.length > 0) return

    countriesLoading.value = true
    countriesError.value = null

    try {
      countries.value = await fetchCountries()
    } catch (error) {
      countriesError.value = 'Fehler beim Laden der Länder'
      console.error('Failed to load countries:', error)
    } finally {
      countriesLoading.value = false
    }
  }

  /**
   * Load subdivisions for a country
   */
  const loadSubdivisions = async (countryCode: string) => {
    if (!countryCode) {
      subdivisions.value = []
      currentCountryCode.value = ''
      return
    }

    const normalizedCode = countryCode.toUpperCase()

    // Skip if already loaded for this country
    if (currentCountryCode.value === normalizedCode && subdivisions.value.length > 0) {
      return
    }

    subdivisionsLoading.value = true
    subdivisionsError.value = null
    currentCountryCode.value = normalizedCode

    try {
      subdivisions.value = await fetchSubdivisions(normalizedCode)
    } catch (error) {
      subdivisionsError.value = 'Fehler beim Laden der Bundesländer'
      subdivisions.value = []
      console.error('Failed to load subdivisions:', error)
    } finally {
      subdivisionsLoading.value = false
    }
  }

  /**
   * Clear subdivisions (useful when country changes)
   */
  const clearSubdivisions = () => {
    subdivisions.value = []
    currentCountryCode.value = ''
  }

  return {
    // Countries
    countries,
    countriesLoading,
    countriesError,
    loadCountries,

    // Subdivisions
    subdivisions,
    subdivisionsLoading,
    subdivisionsError,
    loadSubdivisions,
    clearSubdivisions,
    currentCountryCode,

    // Helpers
    getCallingCodeForCountry,
    getCountryDataByCode,
    getAllCountryCodesData
  }
}

/**
 * Clear all caches (useful for testing or when data might have changed)
 */
export function clearGeoCache() {
  countriesCache = null
  countriesPromise = null
  subdivisionsCache.clear()
  subdivisionsPromises.clear()
}
